/**
 * Leads: inquiries from the website form, social media links, the booking page and the team, from
 * first message until they book, move to onboarding or are closed. Plus which channels bring them.
 */
import { LEAD_SERVICES, LEAD_SOURCES, LEAD_STATUSES, replyPromise } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarPlus, Link2, Mail, Phone, Plus, UserCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { ask, askText } from "@/components/dialogs";
import { Empty, Loading, PageHeader, Pager, Section, Segmented, StatRow, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { toast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { BookForClient } from "@/pages/staff/meetings";

type Status = keyof typeof LEAD_STATUSES;
type Lead = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  kind: "individual" | "business";
  company: string | null;
  services: string[];
  message: string | null;
  source: string;
  campaign: string | null;
  page: string | null;
  referredBy: string | null;
  status: Status;
  lostReason: string | null;
  assignedUserId: string | null;
  clientId: string | null;
  followUpOn: string | null;
  submissions: number;
  firstContactedAt: string | null;
  createdAt: string;
  /** The follow-up time in force when the lead arrived. */
  followUpHours: number;
  /** New, and not contacted within its follow-up time. */
  onFollowUpList?: boolean;
  assignedName?: string | null;
  clientName?: string | null;
  clientStatus?: string | null;
};
type Detail = Lead & { events: Array<{ id: string; kind: string; text: string; at: string; userName: string | null }>; client: { id: string; name: string; status: string } | null };
type Setup = { settings: { followUpHours: number }; staff: Array<{ userId: string; name: string }>; canManage: boolean };
type View = "open" | "follow_up" | "onboarding" | "lost" | "spam" | "all";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const sourceLabel = (s: string) => LEAD_SOURCES[s as keyof typeof LEAD_SOURCES] ?? s;
const serviceLabel = (s: string) => LEAD_SERVICES[s as keyof typeof LEAD_SERVICES] ?? s;
const STATUS_TONE: Record<Status, string> = {
  new: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
  contacted: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  meeting_booked: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
  onboarding: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  lost: "bg-muted text-muted-foreground",
  spam: "bg-muted text-muted-foreground",
};

function StatusBadge({ status }: { status: Status }) {
  return <span className={cn("whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", STATUS_TONE[status])}>{LEAD_STATUSES[status]}</span>;
}

/** "3 h ago", "2 days ago". */
function ago(iso: string) {
  const h = Math.max(0, (Date.now() - Date.parse(iso)) / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min ago`;
  if (h < 36) return `${Math.round(h)} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

export function LeadsPage({ me }: { me: Me }) {
  const search = useSearch();
  const [, navigate] = useLocation();
  const [view, setView] = useState<View>("open");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const openId = new URLSearchParams(search).get("lead");
  const sp = useServerPaged(`${view}|${source}|${q.trim()}`);
  const qs = new URLSearchParams({ view, ...(source ? { source } : {}), ...(q.trim() ? { q: q.trim() } : {}), offset: String(sp.offset), limit: String(sp.size) }).toString();
  const { data, isLoading } = useQuery<{ rows: Lead[]; total: number; counts: Partial<Record<Status, number>>; waiting: number }>({ queryKey: [`/api/leads?${qs}`], placeholderData: (prev) => prev });
  const { data: setup } = useQuery<Setup>({ queryKey: ["/api/leads/setup"] });
  const canEdit = hasPermission(me, "clients.write");
  const c = data?.counts ?? {};
  const views: Array<[View, string, number | undefined]> = [
    ["open", "Open", (c.new ?? 0) + (c.contacted ?? 0) + (c.meeting_booked ?? 0)],
    ["follow_up", "Follow-up list", data?.waiting],
    ["onboarding", "Onboarding", c.onboarding],
    ["lost", "Closed", c.lost],
    ["spam", "Spam", c.spam],
    ["all", "All", (c.new ?? 0) + (c.contacted ?? 0) + (c.meeting_booked ?? 0) + (c.onboarding ?? 0) + (c.lost ?? 0)],
  ];
  const open = (id: string | null) => navigate(id ? `/clients/leads?lead=${id}` : "/clients/leads", { replace: true });

  return (
    <>
      <PageHeader
        title="Leads"
        description="Inquiries from your website, social media, the booking page and the team, until they book or become clients."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link href="/settings?tab=leads">
                <Link2 className="h-4 w-4" /> Links & website form
              </Link>
            </Button>
            {canEdit && (
              <Button onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" /> Add lead
              </Button>
            )}
          </>
        }
      />
      <StatRow
        stats={[
          { label: "New", value: c.new ?? 0, tone: data?.waiting ? "warning" : "default" },
          { label: "Contacted", value: c.contacted ?? 0 },
          { label: "Meeting booked", value: c.meeting_booked ?? 0 },
          { label: "Moved to onboarding", value: c.onboarding ?? 0, tone: "success" },
        ]}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented label="Leads" value={view} onChange={setView} options={views.map(([k, label, n]) => ({ value: k, label, count: n }))} />
        <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
          <select aria-label="Source" className={cn(select, "min-w-0 flex-1 sm:flex-none")} value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">All sources</option>
            {Object.entries(LEAD_SOURCES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <Input aria-label="Search leads" placeholder="Search name or email" className="h-9 min-w-0 flex-1 sm:w-56 sm:flex-none" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      {isLoading ? (
        <Loading />
      ) : !data?.rows.length ? (
        <Empty>
          {view === "open"
            ? "No open leads. Share your inquiry form from Firm Settings › Client communication › Leads."
            : view === "follow_up"
              ? `No leads on the follow-up list. A new lead nobody has contacted within ${setup?.settings.followUpHours ?? 48} hours appears here.`
              : "Nothing here."}
        </Empty>
      ) : (
        <div className="overflow-hidden rounded-xl border border-card-border bg-card shadow-[var(--card-shadow)]">
          {view === "follow_up" && (
            <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
              New leads nobody has contacted within their follow-up time. The same list goes out in the weekday morning email. Each lead keeps the follow-up time in force when it arrived.
            </p>
          )}
          <ul className="divide-y divide-border">
            {data.rows.map((l) => (
              <li key={l.id}>
                <button type="button" onClick={() => open(l.id)} className="flex w-full flex-col gap-1.5 px-4 py-3 text-left hover:bg-muted/50 sm:flex-row sm:items-center sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium">{l.name}</span>
                      {l.company && <span className="text-sm text-muted-foreground">· {l.company}</span>}
                      {l.clientStatus === "active" && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Client</span>}
                      {l.submissions > 1 && <span className="text-xs text-muted-foreground">{l.submissions} messages</span>}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {sourceLabel(l.source)}
                      {l.services.length ? ` · ${l.services.map(serviceLabel).join(", ")}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:justify-end">
                    {l.assignedName && <span>{l.assignedName}</span>}
                    {l.followUpOn && <span>Follow up {formatDate(l.followUpOn)}</span>}
                    <span className={cn(l.onFollowUpList && "font-medium text-amber-700 dark:text-amber-300")}>
                      {ago(l.createdAt)}
                      {l.onFollowUpList && ` · no reply in ${l.followUpHours} h`}
                    </span>
                    <StatusBadge status={l.status} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <Pager paged={sp.paged(data.total)} noun="leads" className="px-4 pb-3" />
        </div>
      )}
      {/* Below the list: shown once the list is in, so it never gets pushed down. */}
      {!isLoading && <SourceReport />}
      {openId && <LeadDialog id={openId} me={me} staff={setup?.staff ?? []} canManage={!!setup?.canManage} onClose={() => open(null)} />}
      {adding && <AddLead staff={setup?.staff ?? []} onClose={(id) => (setAdding(false), id && open(id))} />}
    </>
  );
}

/** Which channels bring leads, and how far those leads get. */
function SourceReport() {
  const [days, setDays] = useState(90);
  const { data } = useQuery<{ sources: Array<{ source: string; leads: number; contacted: number; booked: number; onboarding: number; clients: number }> }>({ queryKey: [`/api/leads/summary?days=${days}`] });
  const max = Math.max(1, ...(data?.sources ?? []).map((s) => s.leads));
  return (
    <Section
      className="mt-6"
      title="Where leads come from"
      description="Spam is left out. Became a client: they're an active client now."
      action={
        <select aria-label="Period" className={select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
      }
    >
      {!data ? (
        <Loading />
      ) : !data.sources.length ? (
        <p className="text-sm text-muted-foreground">No leads in this period yet.</p>
      ) : (
        <ul className="space-y-3">
          {data.sources.map((s) => (
            <li key={s.source}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{sourceLabel(s.source)}</span>
                <span className="tabular text-muted-foreground">{s.leads} lead{s.leads === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary/70" style={{ width: `${(s.leads / max) * 100}%` }} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {s.contacted} contacted · {s.booked} booked a meeting · {s.onboarding} onboarding · {s.clients} became client{s.clients === 1 ? "" : "s"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function LeadDialog({ id, me, staff, canManage, onClose }: { id: string; me: Me; staff: Setup["staff"]; canManage: boolean; onClose: () => void }) {
  const [, navigate] = useLocation();
  const { data: l, isLoading, error } = useQuery<Detail>({ queryKey: [`/api/leads/${id}`] });
  const canEdit = hasPermission(me, "clients.write");
  const [note, setNote] = useState("");
  const [contacted, setContacted] = useState(false);
  const [booking, setBooking] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => setContacted(l?.status === "new"), [l?.status]);
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/leads/${id}`, body) });
  const addNote = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/leads/${id}/notes`, { text: note, contacted }),
    onSuccess: () => setNote(""),
  });
  const book = useMutation({ mutationFn: () => api.send<{ clientId: string; name: string }>("POST", `/api/leads/${id}/client`), onSuccess: (r) => setBooking({ id: r.clientId, name: r.name }) });
  const onboard = useMutation({
    mutationFn: () => api.send<{ clientId: string }>("POST", `/api/leads/${id}/onboard`),
    onSuccess: (r) => {
      toast({ title: "Moved to onboarding", description: "Send the engagement letter, intake and portal invitation from their onboarding page." });
      navigate(`/clients/onboarding/${r.clientId}`);
    },
  });
  const remove = useMutation({ mutationFn: () => api.send("DELETE", `/api/leads/${id}`), onSuccess: onClose });

  if (booking) return <BookForClient preset={booking} onClose={() => setBooking(null)} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {isLoading || !l ? (
          error ? <p className="text-sm text-destructive">{errorMessage(error)}</p> : <Loading />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
                {l.name}
                <StatusBadge status={l.status} />
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 text-sm">
              <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {l.company && <Field label="Business">{l.company}</Field>}
                {l.email && (
                  <Field label="Email">
                    <a className="inline-flex items-center gap-1 break-all text-primary hover:underline" href={`mailto:${l.email}`}>
                      <Mail className="h-3.5 w-3.5 shrink-0" /> {l.email}
                    </a>
                  </Field>
                )}
                {l.phone && (
                  <Field label="Phone">
                    <a className="inline-flex items-center gap-1 text-primary hover:underline" href={`tel:${l.phone}`}>
                      <Phone className="h-3.5 w-3.5" /> {l.phone}
                    </a>
                  </Field>
                )}
                <Field label="Came from">
                  {sourceLabel(l.source)}
                  {l.campaign ? ` · ${l.campaign}` : ""}
                  {l.referredBy ? ` · referred by ${l.referredBy}` : ""}
                </Field>
                <Field label="Received">{formatDateTime(l.createdAt)}</Field>
                {l.status === "new" && <Field label="Follow-up list">{l.onFollowUpList ? `On it: not contacted within ${l.followUpHours} hours` : `After ${l.followUpHours} hours without contact`}</Field>}
                {l.services.length > 0 && <Field label="Interested in">{l.services.map(serviceLabel).join(", ")}</Field>}
                {l.client && (
                  <Field label={l.client.status === "prospect" ? "Prospect" : "Client"}>
                    <Link className="text-primary hover:underline" href={l.client.status === "prospect" ? `/clients/onboarding/${l.client.id}` : `/clients/${l.client.id}`}>
                      {l.client.name}
                    </Link>
                  </Field>
                )}
                {l.status === "lost" && l.lostReason && <Field label="Closed because">{l.lostReason}</Field>}
              </div>

              {canEdit && l.status !== "spam" && (
                <div className="flex flex-wrap gap-2">
                  {l.status === "new" && (
                    <Button size="sm" variant="outline" disabled={patch.isPending} onClick={() => patch.mutate({ status: "contacted" })}>
                      <UserCheck className="h-4 w-4" /> Mark contacted
                    </Button>
                  )}
                  {l.status !== "onboarding" && (
                    <Button size="sm" variant="outline" disabled={book.isPending} onClick={() => book.mutate()}>
                      <CalendarPlus className="h-4 w-4" /> Book a meeting
                    </Button>
                  )}
                  {l.status !== "onboarding" && (
                    <Button size="sm" disabled={onboard.isPending} onClick={() => void ask({ title: `Move ${l.name} to onboarding?`, description: "They'll be added as a prospect (if they aren't already) and appear on the Onboarding list.", confirmLabel: "Move to onboarding" }).then((ok) => ok && onboard.mutate())}>
                      Move to onboarding
                    </Button>
                  )}
                  {["new", "contacted", "meeting_booked"].includes(l.status) && (
                    <Button size="sm" variant="ghost" onClick={() => void askText({ title: "Close this lead?", label: "Reason (optional)", placeholder: "e.g. Went with another firm", confirmLabel: "Close lead" }).then((reason) => reason !== null && patch.mutate({ status: "lost", lostReason: reason }))}>
                      Close
                    </Button>
                  )}
                  {(l.status === "lost" || l.status === "onboarding") && (
                    <Button size="sm" variant="ghost" onClick={() => patch.mutate({ status: "contacted" })}>
                      Reopen
                    </Button>
                  )}
                  {l.status === "new" && (
                    <Button size="sm" variant="ghost" onClick={() => patch.mutate({ status: "spam" })}>
                      Spam
                    </Button>
                  )}
                </div>
              )}
              {canEdit && l.status === "spam" && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => patch.mutate({ status: "new" })}>
                    Not spam
                  </Button>
                </div>
              )}
              {(onboard.error || book.error || patch.error) && <p className="text-destructive">{errorMessage(onboard.error ?? book.error ?? patch.error)}</p>}

              {canEdit && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="ld-assign">Assigned to</Label>
                    <select id="ld-assign" className={cn(select, "w-full")} value={l.assignedUserId ?? ""} onChange={(e) => patch.mutate({ assignedUserId: e.target.value || null })}>
                      <option value="">Nobody</option>
                      {staff.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ld-follow">Follow up on</Label>
                    <Input id="ld-follow" type="date" value={l.followUpOn ?? ""} onChange={(e) => patch.mutate({ followUpOn: e.target.value || null })} />
                  </div>
                </div>
              )}

              {canEdit && (
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addNote.mutate();
                  }}
                >
                  <Label htmlFor="ld-note">Add a note</Label>
                  <Textarea id="ld-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called, they'll send last year's return" />
                  <div className="flex flex-wrap items-center gap-3">
                    <Button size="sm" type="submit" disabled={!note.trim() || addNote.isPending}>
                      Save note
                    </Button>
                    {l.status === "new" && (
                      <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={contacted} onChange={(e) => setContacted(e.target.checked)} /> I contacted them
                      </label>
                    )}
                  </div>
                  {addNote.error && <p className="text-destructive">{errorMessage(addNote.error)}</p>}
                </form>
              )}

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</h3>
                <ol className="space-y-3 border-l border-border pl-4">
                  {l.events.map((e) => (
                    <li key={e.id} className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary/60" aria-hidden />
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(e.at)}
                        {e.userName ? ` · ${e.userName}` : ""} · {EVENT[e.kind] ?? e.kind}
                      </p>
                      {e.text && <p className="mt-0.5 whitespace-pre-wrap break-words">{e.text}</p>}
                    </li>
                  ))}
                </ol>
              </div>

              {canManage && (
                <div className="border-t border-border pt-3">
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void ask({ title: "Delete this lead?", description: "It's removed for good, with its history. Use this for spam, or when someone asks to be forgotten.", confirmLabel: "Delete", destructive: true }).then((ok) => ok && remove.mutate())}>
                    Delete lead
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EVENT: Record<string, string> = { submitted: "Message", status: "Stage", note: "Note", assigned: "Assigned", follow_up: "Follow-up", booked: "Meeting booked" };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="break-words">{children}</div>
    </div>
  );
}

/** A lead from a phone call, a walk-in or an email. */
function AddLead({ staff, onClose }: { staff: Setup["staff"]; onClose: (id?: string) => void }) {
  const [v, setV] = useState({ name: "", email: "", phone: "", kind: "individual", company: "", source: "staff", referredBy: "", message: "", assignedUserId: "" });
  const [services, setServices] = useState<string[]>([]);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ id: string }>("POST", "/api/leads", { ...v, services, assignedUserId: v.assignedUserId || null }),
    onSuccess: (r) => onClose(r.id),
  });
  const field = (k: keyof typeof v, label: string, type = "text") => (
    <div className="space-y-1">
      <Label htmlFor={`al-${k}`}>{label}</Label>
      <Input id={`al-${k}`} type={type} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} required={k === "name"} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add a lead</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {field("name", "Name")}
            <div className="space-y-1">
              <Label htmlFor="al-kind">For</Label>
              <select id="al-kind" className={cn(select, "w-full")} value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })}>
                <option value="individual">An individual or family</option>
                <option value="business">A business</option>
              </select>
            </div>
            {v.kind === "business" && field("company", "Business name")}
            {field("email", "Email (optional)", "email")}
            {field("phone", "Phone (optional)", "tel")}
            <div className="space-y-1">
              <Label htmlFor="al-source">Came from</Label>
              <select id="al-source" className={cn(select, "w-full")} value={v.source} onChange={(e) => setV({ ...v, source: e.target.value })}>
                {Object.entries(LEAD_SOURCES)
                  .filter(([k]) => k !== "booking")
                  .map(([k, label]) => (
                    <option key={k} value={k}>
                      {k === "staff" ? "Phone call or walk-in" : label}
                    </option>
                  ))}
              </select>
            </div>
            {v.source === "referral" && field("referredBy", "Referred by")}
            <div className="space-y-1">
              <Label htmlFor="al-assign">Assigned to</Label>
              <select id="al-assign" className={cn(select, "w-full")} value={v.assignedUserId} onChange={(e) => setV({ ...v, assignedUserId: e.target.value })}>
                <option value="">Nobody</option>
                {staff.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <fieldset>
            <legend className="mb-1.5 font-medium">Interested in</legend>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(LEAD_SERVICES).map(([k, label]) => (
                <button key={k} type="button" aria-pressed={services.includes(k)} onClick={() => setServices((x) => (x.includes(k) ? x.filter((y) => y !== k) : [...x, k]))} className={cn("rounded-full border px-2.5 py-1 text-xs", services.includes(k) ? "border-primary bg-primary/10 font-medium text-primary" : "border-border bg-card hover:bg-muted")}>
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="space-y-1">
            <Label htmlFor="al-message">Notes (optional)</Label>
            <Textarea id="al-message" rows={3} value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} />
          </div>
          {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
          <Button type="submit" disabled={save.isPending}>
            Add lead
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type FullSetup = {
  settings: { notifyUserIds: string[]; autoReply: boolean; followUpHours: number };
  followUpChoices: number[];
  replyPromise: string;
  followUpChanged: { at: string; byUpdate: boolean; byName: string | null; from: number; to: number } | null;
  staff: Array<{ userId: string; name: string; role: string }>;
  formUrl: string;
  bookUrl: string;
  links: Array<{ source: string; label: string; form: string; book: string }>;
  embedCode: string;
  thumbtack: { connected: boolean; connectedAt: string | null; lastLeadAt: string | null };
  canManage: boolean;
};

/** A Copy button for a link or code, with a toast to say it worked. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={label}
      onClick={() =>
        void navigator.clipboard.writeText(text).then(
          () => toast({ title: "Copied" }),
          () => toast({ title: "Couldn't copy", description: "Select the text and copy it instead." }),
        )
      }
    >
      Copy
    </Button>
  );
}

/** The tracked form and booking link for each channel (built by the API from LINK_SOURCES), each with Copy. */
export function LeadChannelLinks({ links, only, labels }: { links: FullSetup["links"]; only?: string[]; labels?: Record<string, string> }) {
  return (
    <ul className="-m-5 divide-y divide-border">
      {links
        .filter((l) => !only || only.includes(l.source))
        .map((l) => (
          <li key={l.source} className="space-y-2 px-5 py-3 text-sm">
            <p className="font-medium">{labels?.[l.source] ?? l.label}</p>
            {(
              [
                ["Form", l.form],
                ["Booking", l.book],
              ] as const
            ).map(([kind, url]) => (
              <div key={kind} className="flex min-w-0 items-center gap-2">
                <span className="w-16 shrink-0 text-xs text-muted-foreground">{kind}</span>
                <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{url}</code>
                <CopyButton text={url} label={`Copy ${l.label} ${kind.toLowerCase()} link`} />
              </div>
            ))}
          </li>
        ))}
    </ul>
  );
}

/** Firm Settings › Client communication › Leads: the links to share, the website code, and who hears about new leads. */
export function LeadSettings() {
  const { data } = useQuery<FullSetup>({ queryKey: ["/api/leads/setup"] });
  if (!data) return <Loading />;
  return (
    <div className="space-y-6">
      <Section title="Your inquiry form" description="Anyone can send you a message from it; each one becomes a lead. Share the link, or put the form on your website.">
        <div className="space-y-2 text-sm">
          <div className="flex min-w-0 items-center gap-2">
            <a className="min-w-0 flex-1 truncate text-primary hover:underline" href={data.formUrl} target="_blank" rel="noreferrer">
              {data.formUrl}
            </a>
            <CopyButton text={data.formUrl} label="Copy form link" />
          </div>
        </div>
      </Section>
      <Section title="Links for each channel" description="Use these so each lead shows where it came from. The form link is for a 'Contact us' button; the booking link is for 'Book now'.">
        <LeadChannelLinks links={data.links} />
      </Section>
      <Section title="Put the form on your website" description="For Weebly: edit the Contact page, drag an Embed Code element onto it, click it, choose Edit Custom HTML, paste this code, then Publish.">
        <div className="space-y-2">
          <Textarea readOnly rows={5} className="font-mono text-xs" value={data.embedCode} aria-label="Website code" onFocus={(e) => e.currentTarget.select()} />
          <CopyButton text={data.embedCode} label="Copy website code" />
        </div>
      </Section>
      <FollowUpTime data={data} />
      <Thumbtack data={data} />
      <Notifications data={data} />
    </div>
  );
}

/** Thumbtack sends each new lead to a private address; the address is shown once. */
function Thumbtack({ data }: { data: FullSetup }) {
  const [url, setUrl] = useState<string | null>(null);
  const t = data.thumbtack;
  const connect = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ url: string }>("POST", "/api/leads/thumbtack"),
    onSuccess: (r) => setUrl(r.url),
  });
  const disconnect = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("DELETE", "/api/leads/thumbtack"),
    onSuccess: () => {
      setUrl(null);
      toast({ title: "Thumbtack disconnected" });
    },
  });
  const err = connect.error ?? disconnect.error;
  return (
    <Section title="Thumbtack" description="Once Thumbtack sends your leads to ClientFlow, each new one appears here as soon as a customer contacts you there, marked as from Thumbtack. You still reply to them in Thumbtack.">
      <div className="space-y-3 text-sm">
        <p>
          {t.connected ? (
            <>
              <span className="font-medium text-emerald-700 dark:text-emerald-400">Connected</span> since {formatDate(t.connectedAt)}. {t.lastLeadAt ? `Last lead received ${formatDateTime(t.lastLeadAt)}.` : "No leads received yet."}
            </>
          ) : (
            <span className="text-muted-foreground">Not connected.</span>
          )}
        </p>
        {url && (
          <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
            <p className="font-medium">Your Thumbtack address. Copy it now: it won't be shown again.</p>
            <div className="flex min-w-0 items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 text-xs">{url}</code>
              <CopyButton text={url} label="Copy Thumbtack address" />
            </div>
            <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
              <li>Thumbtack decides where a business's leads are sent. Email Thumbtack support (or your Thumbtack partner contact) from the account that owns your business profile.</li>
              <li>Ask them to send your new leads (the NegotiationCreatedV4 lead webhook) to this address, and paste it into the email.</li>
              <li>Once they confirm, the next lead appears on the Leads page and the people chosen under Emails are told.</li>
            </ol>
          </div>
        )}
        {err && <p className="text-destructive">{errorMessage(err)}</p>}
        {data.canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={t.connected ? "outline" : "default"}
              disabled={connect.isPending}
              onClick={() =>
                t.connected
                  ? void ask({ title: "Create a new Thumbtack address?", description: "The current address stops working at once. Give the new one to Thumbtack, or leads will stop arriving.", confirmLabel: "Create new address" }).then((ok) => ok && connect.mutate())
                  : connect.mutate()
              }
            >
              {t.connected ? "Create a new address" : "Connect Thumbtack"}
            </Button>
            {t.connected && (
              <Button size="sm" variant="ghost" className="text-destructive" disabled={disconnect.isPending} onClick={() => void ask({ title: "Disconnect Thumbtack?", description: "Leads Thumbtack sends after this are refused. Leads already received stay.", confirmLabel: "Disconnect", destructive: true }).then((ok) => ok && disconnect.mutate())}>
                Disconnect
              </Button>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Only owners and admins can connect Thumbtack.</p>
        )}
      </div>
    </Section>
  );
}

/** When a new lead nobody has contacted goes on the follow-up list; the reply promise follows it. */
function FollowUpTime({ data }: { data: FullSetup }) {
  const [hours, setHours] = useState(data.settings.followUpHours);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", "/api/leads/settings", { followUpHours: hours }),
    onSuccess: () => toast({ title: "Follow-up time saved", description: `Leads that arrive from now on go on the follow-up list after ${hours} hours.` }),
  });
  // A time saved before the choices were limited stays listed until it is changed.
  const choices = data.followUpChoices.includes(data.settings.followUpHours) ? data.followUpChoices : [...data.followUpChoices, data.settings.followUpHours].sort((a, b) => a - b);
  const changed = data.followUpChanged;
  return (
    <Section title="Follow-up time" description="A new lead nobody has contacted within this time goes on the follow-up list (Clients › Leads) and in the weekday morning email.">
      <form
        className="space-y-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="ls-hours">Follow-up list after</Label>
          <select id="ls-hours" disabled={!data.canManage} className={cn(select, "w-full sm:w-64")} value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {choices.map((h) => (
              <option key={h} value={h}>
                {h} hours without contact
              </option>
            ))}
          </select>
        </div>
        <p className="text-muted-foreground">
          Your thank-you email and inquiry form say you'll reply <span className="font-medium text-foreground">{replyPromise(hours)}</span>.
        </p>
        <p className="text-xs text-muted-foreground">A change applies to leads that arrive after it. Earlier leads keep the time in force when they arrived, and leads already on the follow-up list stay on it.</p>
        {changed && (
          <p className="text-xs text-muted-foreground">
            Last changed from {changed.from} to {changed.to} hours{changed.byUpdate ? " by a ClientFlow update" : changed.byName ? ` by ${changed.byName}` : ""} on {formatDateTime(changed.at)}.
          </p>
        )}
        {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
        {data.canManage ? (
          <Button type="submit" size="sm" disabled={save.isPending || hours === data.settings.followUpHours}>
            Save
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Only owners and admins can change this.</p>
        )}
      </form>
    </Section>
  );
}

function Notifications({ data }: { data: FullSetup }) {
  const [v, setV] = useState({ notifyUserIds: data.settings.notifyUserIds, autoReply: data.settings.autoReply });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", "/api/leads/settings", v),
    onSuccess: () => toast({ title: "Lead settings saved" }),
  });
  const owners = data.staff.filter((m) => m.role === "owner").map((m) => m.name);
  return (
    <Section title="Emails" description="Who hears about each new lead, and what the person gets back. Each weekday morning, the same people get the follow-up list and the follow-ups due.">
      <form
        className="space-y-4 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <fieldset disabled={!data.canManage} className="space-y-4">
          <div>
            <p className="mb-1.5 font-medium">Tell these people about new leads</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {data.staff.map((m) => (
                <label key={m.userId} className="flex items-center gap-2">
                  <input type="checkbox" checked={v.notifyUserIds.includes(m.userId)} onChange={(e) => setV({ ...v, notifyUserIds: e.target.checked ? [...v.notifyUserIds, m.userId] : v.notifyUserIds.filter((x) => x !== m.userId) })} /> {m.name}
                </label>
              ))}
            </div>
            {!v.notifyUserIds.length && <p className="mt-1 text-xs text-muted-foreground">Nobody chosen: the owner{owners.length > 1 ? "s" : ""} ({owners.join(", ")}) get them.</p>}
          </div>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={v.autoReply} onChange={(e) => setV({ ...v, autoReply: e.target.checked })} />
            <span>
              Thank each person by email straight away
              <span className="block text-xs text-muted-foreground">With a link to book a time, and saying you'll reply {data.replyPromise} (from the follow-up time). At most one a day per address.</span>
            </span>
          </label>
        </fieldset>
        {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
        {data.canManage ? (
          <Button type="submit" size="sm" disabled={save.isPending}>
            Save
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Only owners and admins can change these.</p>
        )}
      </form>
    </Section>
  );
}
